import React, { useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  PaperClipIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import EntityDrawer, { SecondaryPanel } from '../ui/EntityDrawer';
import Tabs from '../ui/Tabs';
import InvoiceEditorForm, { InvoiceEditorLineItem } from '../InvoiceEditorForm';
import CustomerTypeahead, { CustomerTypeaheadOption } from '../customer/CustomerTypeahead';
import AccountTypeahead, { AccountTypeaheadOption } from '../account/AccountTypeahead';
import ReservationTypeahead, { ReservationTypeaheadOption } from '../reservations/ReservationTypeahead';
import ReservationEditorPanel from '../reservations/ReservationEditorPanel';
import RecordContactSnapshotsPanel from '../contacts/RecordContactSnapshotsPanel';
import { Invoice, InvoiceDisplayStatus, InvoicePaymentActivity } from '../../types/invoice';
import {
  formatCurrencyCents,
  formatDateShort,
  formatLineItemCount,
  normalizeInvoiceStatus,
} from '../../utils/invoiceFormatting';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import InvoiceContactEditor from './InvoiceContactEditor';
import { adminAPI, apiClient } from '../../services/api';

type InvoiceDrawerTab = 'details' | 'activity' | 'files';

const MANUAL_PAYMENT_METHOD_OPTIONS = [
  'cash',
  'check',
  'ach',
  'wire',
  'other',
] as const;

type FeeBreakdown = {
  stripeFee: number | null;
  platformFee: number | null;
  totalFees: number | null;
  netAmount: number | null;
  amountPaid: number;
  source: 'live' | 'local';
} | null;

function getEffectiveInvoiceCustomerId(invoice: Pick<Invoice, 'customer_id' | 'metadata'>): string | null {
  return invoice.metadata?.local_customer_id || null;
}

function SkeletonLine({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-surface-container-low ${className}`.trim()} />;
}

function SkeletonBadge({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-full bg-surface-container-low ${className}`.trim()} />;
}

function humanizeWebhookEvent(eventType: string): string {
  const map: Record<string, string> = {
    'invoice.created': 'Invoice created',
    'invoice.finalized': 'Invoice finalized',
    'invoice.sent': 'Invoice sent',
    'invoice.payment_succeeded': 'Payment received',
    'invoice.payment_failed': 'Payment failed',
    'invoice.payment_action_required': 'Payment action required',
    'invoice.voided': 'Invoice voided',
    'invoice.marked_uncollectible': 'Marked uncollectible',
    'invoice.updated': 'Invoice updated',
    'invoice.deleted': 'Invoice deleted',
    'payment_intent.created': 'Payment initiated',
    'payment_intent.succeeded': 'Payment confirmed',
    'payment_intent.payment_failed': 'Payment failed',
    'payment_intent.canceled': 'Payment cancelled',
    'charge.succeeded': 'Charge succeeded',
    'charge.failed': 'Charge failed',
    'charge.refunded': 'Refund processed',
  };
  return map[eventType] ?? eventType.replace(/\./g, ' · ').replace(/_/g, ' ');
}

type Props = {
  open: boolean;
  selectedInvoice: Invoice;
  syncWarning?: React.ReactNode;
  paymentActivityLoading: boolean;
  paymentActivity: InvoicePaymentActivity | null;
  feeBreakdown: FeeBreakdown;
  formatActivityTimestamp: (value: string | number | null | undefined) => string;
  activitySortValue: (value: string | number | null | undefined) => number;
  getStripeObjectUrl: (objectId: string | null | undefined, connectedAccountId?: string | null) => string | null;
  onClose: () => void;
  onAction: (label: string) => void;
  actions: Array<{ label: string; dangerous?: boolean; onClick: () => void }>;
  onUploadFileClick: () => void;
  uploadingAttachment: boolean;
  onAttachmentDownload: (attachmentId: string, originalName: string) => void;
  onAttachmentDelete: (attachmentId: string) => void;
  formatFileSize: (bytes: number) => string;
  isPlatformAdmin: boolean;
  onSaveEdit: (payload: {
    line_items: Array<{ description: string; quantity: number; unit_amount: number }>;
    tax_rate: number;
    discount_amount: number;
    payment_terms: string;
    due_date?: string;
    memo: string;
    description: string;
  }) => Promise<void>;
  onMarkPaidSubmit?: (payload: { paymentMethod: string; note?: string }) => Promise<void>;
  markPaidSubmitting?: boolean;
  markPaidPanelRequestKey?: number;
};

export default function InvoiceDrawer({
  open,
  selectedInvoice,
  syncWarning,
  paymentActivityLoading,
  paymentActivity,
  feeBreakdown,
  formatActivityTimestamp,
  activitySortValue,
  getStripeObjectUrl,
  onClose,
  onAction,
  actions,
  onUploadFileClick,
  uploadingAttachment,
  onAttachmentDownload,
  onAttachmentDelete,
  formatFileSize,
  isPlatformAdmin,
  onSaveEdit,
  onMarkPaidSubmit,
  markPaidSubmitting = false,
  markPaidPanelRequestKey = 0,
}: Props) {
  const navigate = useNavigate();
  const [drawerTab, setDrawerTab] = useState<InvoiceDrawerTab>('details');
  const [isEditing, setIsEditing] = useState(false);
  const [secondaryView, setSecondaryView] = useState<'customer' | 'account' | 'reservation' | 'markPaid' | null>(null);
  const [markPaidPaymentMethod, setMarkPaidPaymentMethod] = useState<string>(MANUAL_PAYMENT_METHOD_OPTIONS[0]);
  const [markPaidNote, setMarkPaidNote] = useState('');
  const [lastHandledMarkPaidRequestKey, setLastHandledMarkPaidRequestKey] = useState(0);
  const markPaidMethodRef = React.useRef<HTMLSelectElement>(null);

  const [currentCustomerId, setCurrentCustomerId] = useState<string | null>(getEffectiveInvoiceCustomerId(selectedInvoice));
  const [currentAccountId, setCurrentAccountId] = useState<string | null>(selectedInvoice.account_id || null);
  const [currentReservationId, setCurrentReservationId] = useState<string | null>(selectedInvoice.reservation_id || null);
  const [currentCustomerDetails, setCurrentCustomerDetails] = useState(selectedInvoice.customer_details);
  const [contactInvoice, setContactInvoice] = useState(selectedInvoice);

  const [selectedCustomerOption, setSelectedCustomerOption] = useState<CustomerTypeaheadOption | null>(null);
  const [selectedAccountOption, setSelectedAccountOption] = useState<AccountTypeaheadOption | null>(null);
  const [selectedReservationOption, setSelectedReservationOption] = useState<ReservationTypeaheadOption | null>(null);

  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showCreateReservation, setShowCreateReservation] = useState(false);

  const [newCustomerFirstName, setNewCustomerFirstName] = useState('');
  const [newCustomerLastName, setNewCustomerLastName] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [newCustomerCompany, setNewCustomerCompany] = useState('');
  const [newCustomerAccountMode, setNewCustomerAccountMode] = useState<'none' | 'link_existing' | 'create_new'>('none');
  const [newCustomerAccountOption, setNewCustomerAccountOption] = useState<AccountTypeaheadOption | null>(null);
  const [newCustomerAccountName, setNewCustomerAccountName] = useState('');
  const [newCustomerPrimaryBilling, setNewCustomerPrimaryBilling] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');

  const [manualReservationRef, setManualReservationRef] = useState('');

  const [associationSaving, setAssociationSaving] = useState(false);
  const [associationReason, setAssociationReason] = useState('');
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);

  const [editLineItems, setEditLineItems] = useState<InvoiceEditorLineItem[]>([]);
  const [editTaxPercentage, setEditTaxPercentage] = useState(0);
  const [editDiscountAmount, setEditDiscountAmount] = useState(0);
  const [editPaymentTerms, setEditPaymentTerms] = useState('due_on_receipt');
  const [editDueDate, setEditDueDate] = useState('');
  const [editMemo, setEditMemo] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const normalizedStatus: InvoiceDisplayStatus = normalizeInvoiceStatus(selectedInvoice);
  const attachmentCount = selectedInvoice.attachments?.length ?? 0;
  const groupedPaymentLockActive = Boolean(selectedInvoice.grouped_payment_request_is_active);
  const manualPaymentMetadata = selectedInvoice.metadata?.manual_payment === 'true'
    ? selectedInvoice.metadata
    : null;

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const { data: linkedCustomer } = useQuery(
    ['invoice-linked-customer', currentCustomerId],
    async () => {
      const res = await adminAPI.getCustomer(currentCustomerId!);
      return res.data as any | null;
    },
    {
      enabled: open && Boolean(currentCustomerId),
      staleTime: 120_000,
      retry: false,
    },
  );

  // Fetch account name when account_id is present
  const { data: linkedAccount } = useQuery(
    ['invoice-linked-account', currentAccountId],
    async () => {
      const res = await adminAPI.getAccount(currentAccountId!);
      return res.data as { id: string; name: string } | null;
    },
    {
      enabled: open && Boolean(currentAccountId),
      staleTime: 120_000,
      retry: false,
    },
  );

  // Fetch reservation when reservation_id is a UUID and the secondary panel opens
  const reservationIsUuid = Boolean(currentReservationId && uuidRegex.test(currentReservationId));
  const { data: linkedReservation } = useQuery(
    ['invoice-linked-reservation', currentReservationId],
    async () => {
      const res = await adminAPI.getReservation(currentReservationId!);
      return res.data as any | null;
    },
    {
      enabled: open && reservationIsUuid,
      staleTime: 120_000,
      retry: false,
    },
  );

  useEffect(() => {
    if (open) {
      setDrawerTab('details');
      setAssociationReason('');
      setIsEditing(false);
      setSecondaryView(null);
      setCurrentCustomerId(getEffectiveInvoiceCustomerId(selectedInvoice));
      setCurrentAccountId(selectedInvoice.account_id || null);
      setCurrentReservationId(selectedInvoice.reservation_id || null);
      setCurrentCustomerDetails(selectedInvoice.customer_details);
      setContactInvoice(selectedInvoice);

      setSelectedCustomerOption(null);
      setSelectedAccountOption(null);
      setSelectedReservationOption(null);

      setShowCreateCustomer(false);
      setShowCreateAccount(false);
      setShowCreateReservation(false);

      setNewCustomerFirstName('');
      setNewCustomerLastName('');
      setNewCustomerEmail('');
      setNewCustomerPhone('');
      setNewCustomerCompany('');
      setNewAccountName('');
      setManualReservationRef(selectedInvoice.reservation_id || '');
      setMarkPaidPaymentMethod(MANUAL_PAYMENT_METHOD_OPTIONS[0]);
      setMarkPaidNote('');
    }
  }, [open, selectedInvoice.id]);

  useEffect(() => {
    if (!open) return;
    if (!onMarkPaidSubmit) return;
    if (selectedInvoice.status !== 'open') return;
    if (!markPaidPanelRequestKey) return;
    if (markPaidPanelRequestKey <= lastHandledMarkPaidRequestKey) return;

    setSecondaryView('markPaid');
    setLastHandledMarkPaidRequestKey(markPaidPanelRequestKey);
  }, [
    open,
    onMarkPaidSubmit,
    selectedInvoice.status,
    markPaidPanelRequestKey,
    lastHandledMarkPaidRequestKey,
  ]);

  useEffect(() => {
    if (!open) return;
    if (secondaryView !== 'markPaid') return;

    const frame = window.requestAnimationFrame(() => {
      markPaidMethodRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [open, secondaryView]);

  function enterEditMode() {
    setEditLineItems(
      selectedInvoice.line_items.map((li) => ({
        id: li.id,
        description: li.description,
        quantity: li.quantity,
        unit_amount: li.unit_amount / 100,
      }))
    );
    setEditTaxPercentage(selectedInvoice.tax_rate ? Number(selectedInvoice.tax_rate) * 100 : 0);
    setEditDiscountAmount((selectedInvoice.discount_amount ?? 0) / 100);
    setEditPaymentTerms(selectedInvoice.payment_terms || 'due_on_receipt');
    setEditDueDate(
      selectedInvoice.due_date
        ? new Date(selectedInvoice.due_date).toISOString().split('T')[0]
        : ''
    );
    setEditMemo(selectedInvoice.memo || '');
    setEditDescription(selectedInvoice.description || '');
    setIsEditing(true);
  }

  const editSubtotal = editLineItems.reduce(
    (sum, item) => sum + item.quantity * item.unit_amount,
    0
  );
  const editSubtotalAfterDiscount = Math.max(0, editSubtotal - editDiscountAmount);
  const editTaxAmount = editSubtotalAfterDiscount * (editTaxPercentage / 100);
  const editTotal = editSubtotalAfterDiscount + editTaxAmount;

  const formatEditCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  async function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (editLineItems.some((item) => !item.description || item.unit_amount < 0)) {
      toast.error('All line items must have a description and a non-negative amount');
      return;
    }
    setIsSaving(true);
    try {
      await onSaveEdit({
        line_items: editLineItems.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unit_amount: Math.round(item.unit_amount * 100),
        })),
        tax_rate: editTaxPercentage / 100,
        discount_amount: Math.round(editDiscountAmount * 100),
        payment_terms: editPaymentTerms,
        due_date: editDueDate || undefined,
        memo: editMemo,
        description: editDescription,
      });
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }

  const tabs: Array<{ id: InvoiceDrawerTab; label: string }> = [
    { id: 'details', label: 'Details' },
    { id: 'activity', label: 'Activity' },
    { id: 'files', label: attachmentCount > 0 ? `Files (${attachmentCount})` : 'Files' },
  ];

  const linkedCustomerRecord = linkedCustomer?.customer || linkedCustomer || null;
  const linkedCustomerName = linkedCustomerRecord
    ? [linkedCustomerRecord.first_name, linkedCustomerRecord.last_name].filter(Boolean).join(' ').trim()
    : '';

  async function saveAssociations(
    payload: { customer_id?: string | null; account_id?: string | null; reservation_id?: string | null; refresh_customer_details?: boolean },
    successMessage = 'Invoice associations updated',
  ) {
    if (payload.reservation_id === undefined && selectedInvoice.status !== 'draft') return;
    if (associationReason.trim().length < 5) { toast.error('Enter a correction reason of at least 5 characters'); return; }
    setAssociationSaving(true);
    try {
      const res = await apiClient.put(`/invoices/admin/${selectedInvoice.id}/associations`, {
        ...payload,
        reason: associationReason.trim(),
        ...(payload.reservation_id !== undefined ? { expected_reservation_id: currentReservationId || null } : {}),
      });
      const updated = res.data as Invoice;
      setContactInvoice(updated);
      setCurrentCustomerId(getEffectiveInvoiceCustomerId(updated));
      setCurrentAccountId(updated.account_id || null);
      setCurrentReservationId(updated.reservation_id || null);
      setCurrentCustomerDetails(updated.customer_details || selectedInvoice.customer_details);
      setManualReservationRef(updated.reservation_id || '');
      toast.success(successMessage);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update invoice associations');
    } finally {
      setAssociationSaving(false);
    }
  }

  async function createAndAttachCustomer() {
    if (associationReason.trim().length < 5) { toast.error('Enter a correction reason before creating a customer'); return; }
    if (!newCustomerFirstName.trim() || !newCustomerEmail.trim()) {
      toast.error('First name and email are required');
      return;
    }

    setCreatingCustomer(true);
    try {
      if (newCustomerAccountMode === 'link_existing' && !newCustomerAccountOption?.id) {
        toast.error('Select an account for this customer');
        return;
      }
      if (newCustomerAccountMode === 'create_new' && !(newCustomerAccountName.trim() || newCustomerCompany.trim())) {
        toast.error('Enter an account name');
        return;
      }
      const res = await adminAPI.createCustomer({
        email: newCustomerEmail.trim(),
        firstName: newCustomerFirstName.trim(),
        lastName: newCustomerLastName.trim(),
        phone: newCustomerPhone.trim() || undefined,
        companyName: newCustomerCompany.trim() || undefined,
        accountRelationship: {
          mode: newCustomerAccountMode,
          accountId: newCustomerAccountMode === 'link_existing' ? newCustomerAccountOption?.id || null : null,
          accountName: newCustomerAccountMode === 'create_new'
            ? newCustomerAccountName.trim() || newCustomerCompany.trim()
            : null,
          makePrimaryBillingContact: newCustomerPrimaryBilling,
        },
      });

      const created = res.data?.customer || res.data;
      if (!created?.id) {
        throw new Error('Customer was created but no identifier was returned');
      }

      setSelectedCustomerOption({
        id: String(created.id),
        label: [newCustomerFirstName.trim(), newCustomerLastName.trim()].filter(Boolean).join(' ') || newCustomerEmail.trim(),
        description: newCustomerEmail.trim(),
        email: newCustomerEmail.trim(),
      });

      await saveAssociations({ customer_id: String(created.id) }, 'Customer created and linked');
      setShowCreateCustomer(false);
      setNewCustomerFirstName('');
      setNewCustomerLastName('');
      setNewCustomerEmail('');
      setNewCustomerPhone('');
      setNewCustomerCompany('');
      setNewCustomerAccountMode('none');
      setNewCustomerAccountOption(null);
      setNewCustomerAccountName('');
      setNewCustomerPrimaryBilling(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create customer');
    } finally {
      setCreatingCustomer(false);
    }
  }

  async function createAndAttachAccount() {
    if (associationReason.trim().length < 5) { toast.error('Enter a correction reason before creating an account'); return; }
    if (!newAccountName.trim()) {
      toast.error('Account name is required');
      return;
    }

    setCreatingAccount(true);
    try {
      const res = await adminAPI.createAccount({ name: newAccountName.trim() });
      const created = res.data;
      if (!created?.id) {
        throw new Error('Account was created but no identifier was returned');
      }

      setSelectedAccountOption({
        id: String(created.id),
        label: created.name || newAccountName.trim(),
        description: undefined,
      });

      await saveAssociations({ account_id: String(created.id) }, 'Account created and linked');
      setShowCreateAccount(false);
      setNewAccountName('');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create account');
    } finally {
      setCreatingAccount(false);
    }
  }

  async function handleReservationCreated(reservation: any) {
    const reservationId = reservation?.id ? String(reservation.id) : '';
    if (!reservationId) {
      toast.error('Reservation created, but no ID was returned');
      return;
    }

    const createdReservationRef = reservation.reservationRef || reservation.reservation_ref || reservationId.slice(0, 8);
    const createdCustomerName = reservation?.customer?.name || reservation?.tripDetails?.customer_name || null;
    const createdCustomerEmail = reservation?.customer?.email || reservation?.tripDetails?.customer_email || null;

    setSelectedReservationOption({
      id: reservationId,
      label: createdReservationRef,
      description: createdCustomerName || createdCustomerEmail || undefined,
      reservationRef: createdReservationRef,
      startDate: reservation.startDate || reservation.start_date,
      endDate: reservation.endDate || reservation.end_date,
      customerName: createdCustomerName,
    });

    await saveAssociations({ reservation_id: reservationId }, 'Reservation created and linked');
    setShowCreateReservation(false);
  }

  async function submitMarkPaidFromDrawer() {
    if (!onMarkPaidSubmit) return;

    const paymentMethod = markPaidPaymentMethod.trim();
    if (!paymentMethod) {
      toast.error('Payment method is required');
      return;
    }

    try {
      await onMarkPaidSubmit({
        paymentMethod,
        note: markPaidNote.trim() || undefined,
      });
      setSecondaryView(null);
    } catch {
      // Parent handler already surfaces validation/API errors.
    }
  }

  function handleMarkPaidPanelKeyDown(event: React.KeyboardEvent<HTMLFormElement>) {
    if (event.key === 'Escape') {
      if (markPaidSubmitting) return;
      event.preventDefault();
      setSecondaryView(null);
      return;
    }

    if (event.key !== 'Enter') return;

    const submitFromTextarea = event.target instanceof HTMLTextAreaElement && (event.metaKey || event.ctrlKey);
    const submitFromMethodSelect = event.target instanceof HTMLSelectElement;

    if (!submitFromTextarea && !submitFromMethodSelect) return;
    if (markPaidSubmitting) return;

    event.preventDefault();
    void submitMarkPaidFromDrawer();
  }

  const reservationRef =
    linkedReservation?.reservationRef || linkedReservation?.reservation_ref || currentReservationId || null;

  // Build secondary panel config
  const secondary: SecondaryPanel | undefined = secondaryView
    ? {
        open: true,
        onClose: () => setSecondaryView(null),
        title:
          secondaryView === 'customer'
            ? 'Customer'
            : secondaryView === 'account'
            ? 'Account'
            : secondaryView === 'markPaid'
            ? 'Mark Invoice Paid'
            : showCreateReservation
            ? 'New Reservation'
            : 'Reservation',
        subtitle:
          secondaryView === 'customer'
            ? currentCustomerId
              ? `Linked · ${currentCustomerId.slice(0, 8)}…`
              : 'No customer linked yet'
            : secondaryView === 'account'
            ? currentAccountId
              ? `Linked · ${currentAccountId.slice(0, 8)}…`
              : 'No account linked yet'
            : secondaryView === 'markPaid'
            ? `${selectedInvoice.invoice_number} · ${selectedInvoice.customer_details.email}`
            : currentReservationId
            ? `Linked · ${(reservationRef ?? currentReservationId)?.slice(0, 16)}…`
            : 'No reservation linked yet',
        children: (
          <div className="space-y-4">
            {secondaryView === 'customer' && (
              <>
                <div className="card p-4">
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Name</dt>
                      <dd className="text-right font-medium text-on-surface">{currentCustomerDetails?.name || linkedCustomerName || '—'}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Email</dt>
                      <dd className="break-all text-right text-on-surface">{currentCustomerDetails?.email || '—'}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Customer ID</dt>
                      <dd className="font-mono text-xs text-on-surface-variant">{currentCustomerId || '—'}</dd>
                    </div>
                  </dl>
                </div>

                {selectedInvoice.status === 'draft' && <div className="rounded-card border border-ghost bg-surface-container-low p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-on-surface">Change customer association</p>
                    <label className="block text-sm">Correction reason<input className="input mt-1 w-full" value={associationReason} maxLength={1000} onChange={(event) => setAssociationReason(event.target.value)} /></label>
                    <button
                      type="button"
                      onClick={() => setShowCreateCustomer((v) => !v)}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <PlusIcon className="h-3.5 w-3.5" />
                      {showCreateCustomer ? 'Search existing' : 'New customer'}
                    </button>
                  </div>

                  {showCreateCustomer ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          className="input-field text-sm"
                          placeholder="First name *"
                          value={newCustomerFirstName}
                          onChange={(e) => setNewCustomerFirstName(e.target.value)}
                        />
                        <input
                          type="text"
                          className="input-field text-sm"
                          placeholder="Last name"
                          value={newCustomerLastName}
                          onChange={(e) => setNewCustomerLastName(e.target.value)}
                        />
                      </div>
                      <input
                        type="email"
                        className="input-field w-full text-sm"
                        placeholder="Email *"
                        value={newCustomerEmail}
                        onChange={(e) => setNewCustomerEmail(e.target.value)}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          className="input-field text-sm"
                          placeholder="Phone"
                          value={newCustomerPhone}
                          onChange={(e) => setNewCustomerPhone(e.target.value)}
                        />
                        <input
                          type="text"
                          className="input-field text-sm"
                          placeholder="Business / company label"
                          value={newCustomerCompany}
                          onChange={(e) => setNewCustomerCompany(e.target.value)}
                        />
                      </div>
                      <div className="rounded-lg border border-ghost bg-surface-container-lowest p-3">
                        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-on-surface-variant">Account relationship</p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          {[
                            { value: 'none', label: 'None' },
                            { value: 'link_existing', label: 'Link' },
                            { value: 'create_new', label: 'Create' },
                          ].map((option) => (
                            <label
                              key={option.value}
                              className={`rounded-md border px-2 py-2 ${
                                newCustomerAccountMode === option.value
                                  ? 'border-primary bg-primary/10 text-on-surface'
                                  : 'border-outline-variant text-on-surface-variant'
                              }`}
                            >
                              <input
                                type="radio"
                                className="mr-1"
                                checked={newCustomerAccountMode === option.value}
                                onChange={() => setNewCustomerAccountMode(option.value as typeof newCustomerAccountMode)}
                              />
                              {option.label}
                            </label>
                          ))}
                        </div>
                        {newCustomerAccountMode === 'link_existing' ? (
                          <div className="mt-2">
                            <AccountTypeahead
                              value={newCustomerAccountOption}
                              onChange={setNewCustomerAccountOption}
                              placeholder="Search accounts..."
                            />
                          </div>
                        ) : null}
                        {newCustomerAccountMode === 'create_new' ? (
                          <input
                            type="text"
                            className="input-field mt-2 text-sm"
                            placeholder={newCustomerCompany || 'Account name'}
                            value={newCustomerAccountName}
                            onChange={(e) => setNewCustomerAccountName(e.target.value)}
                          />
                        ) : null}
                        {newCustomerAccountMode !== 'none' ? (
                          <label className="mt-2 flex items-center gap-2 text-xs text-on-surface-variant">
                            <input
                              type="checkbox"
                              checked={newCustomerPrimaryBilling}
                              onChange={(e) => setNewCustomerPrimaryBilling(e.target.checked)}
                            />
                            Primary billing contact
                          </label>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="btn-primary w-full"
                        onClick={createAndAttachCustomer}
                        disabled={creatingCustomer || associationSaving}
                      >
                        {creatingCustomer ? 'Creating...' : 'Create and link customer'}
                      </button>
                    </div>
                  ) : (
                    <>
                      <CustomerTypeahead
                        value={selectedCustomerOption}
                        onChange={setSelectedCustomerOption}
                        placeholder="Search customer by name or email..."
                        disabled={associationSaving}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          className="btn-primary"
                          disabled={!selectedCustomerOption || associationSaving}
                          onClick={() => {
                            if (selectedCustomerOption) {
                              saveAssociations({ customer_id: selectedCustomerOption.id }, 'Customer association updated');
                            }
                          }}
                        >
                          Link selected
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={associationSaving || !currentCustomerId}
                          onClick={() => saveAssociations({ customer_id: null }, 'Customer unlinked')}
                        >
                          Clear
                        </button>
                      </div>
                    </>
                  )}
                </div>}

                {currentCustomerId && (
                  <button
                    type="button"
                    className="btn-secondary w-full"
                    onClick={() => {
                      onClose();
                      navigate(`/admin/customers?search=${encodeURIComponent(currentCustomerDetails?.email || '')}`);
                    }}
                  >
                    Open customer page →
                  </button>
                )}
              </>
            )}

            {secondaryView === 'account' && (
              <>
                <div className="card p-4">
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Name</dt>
                      <dd className="font-medium text-on-surface">{linkedAccount?.name ?? '—'}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Account ID</dt>
                      <dd className="font-mono text-xs text-on-surface-variant">{currentAccountId || '—'}</dd>
                    </div>
                  </dl>
                </div>

                {selectedInvoice.status === 'draft' && <div className="rounded-card border border-ghost bg-surface-container-low p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-on-surface">Change account association</p>
                    <label className="block text-sm">Correction reason<input className="input mt-1 w-full" value={associationReason} maxLength={1000} onChange={(event) => setAssociationReason(event.target.value)} /></label>
                    <button
                      type="button"
                      onClick={() => setShowCreateAccount((v) => !v)}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <PlusIcon className="h-3.5 w-3.5" />
                      {showCreateAccount ? 'Search existing' : 'New account'}
                    </button>
                  </div>

                  {showCreateAccount ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        className="input-field w-full text-sm"
                        placeholder="Account name *"
                        value={newAccountName}
                        onChange={(e) => setNewAccountName(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn-primary w-full"
                        onClick={createAndAttachAccount}
                        disabled={creatingAccount || associationSaving}
                      >
                        {creatingAccount ? 'Creating...' : 'Create and link account'}
                      </button>
                    </div>
                  ) : (
                    <>
                      <AccountTypeahead
                        value={selectedAccountOption}
                        onChange={setSelectedAccountOption}
                        placeholder="Search account by name..."
                        disabled={associationSaving}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          className="btn-primary"
                          disabled={!selectedAccountOption || associationSaving}
                          onClick={() => {
                            if (selectedAccountOption) {
                              saveAssociations({ account_id: selectedAccountOption.id }, 'Account association updated');
                            }
                          }}
                        >
                          Link selected
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={associationSaving || !currentAccountId}
                          onClick={() => saveAssociations({ account_id: null }, 'Account unlinked')}
                        >
                          Clear
                        </button>
                      </div>
                    </>
                  )}
                </div>}

                {currentAccountId && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      className="btn-secondary w-full"
                      onClick={() => {
                        onClose();
                        navigate(`/admin/accounts/${currentAccountId}`);
                      }}
                    >
                      Open full account page →
                    </button>
                  </div>
                )}
              </>
            )}

            {secondaryView === 'reservation' && (
              <>
                <div className="card p-4">
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Reference</dt>
                      <dd className="text-right font-medium text-on-surface">{reservationRef || '—'}</dd>
                    </div>
                    {linkedReservation?.status && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-on-surface-variant">Status</dt>
                        <dd className="capitalize text-on-surface">{linkedReservation.status}</dd>
                      </div>
                    )}
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Reservation ID</dt>
                      <dd className="font-mono text-xs text-on-surface-variant">{currentReservationId || '—'}</dd>
                    </div>
                  </dl>
                  <p className="mt-3 text-xs text-on-surface-variant">
                    Multiple invoices can be linked to the same reservation.
                  </p>
                </div>

                <div className="rounded-card border border-ghost bg-surface-container-low p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-on-surface">Change reservation association</p>
                    <label className="block text-sm">Correction reason<input className="input mt-1 w-full" value={associationReason} maxLength={1000} onChange={(event) => setAssociationReason(event.target.value)} /></label>
                    <button
                      type="button"
                      onClick={() => setShowCreateReservation((v) => !v)}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <PlusIcon className="h-3.5 w-3.5" />
                      {showCreateReservation ? 'Search existing' : 'New reservation'}
                    </button>
                  </div>

                  {showCreateReservation ? (
                    <ReservationEditorPanel
                      initialValues={{
                        account: currentAccountId
                          ? { id: currentAccountId, name: linkedAccount?.name || null }
                          : null,
                        customer: currentCustomerId
                          ? {
                              id: currentCustomerId,
                              name: currentCustomerDetails?.name || linkedCustomerName || null,
                              email: currentCustomerDetails?.email || null,
                            }
                          : null,
                        customerCompanyName: linkedAccount?.name || currentCustomerDetails?.company_name || null,
                      }}
                      onSaved={(reservation) => {
                        void handleReservationCreated(reservation);
                        setShowCreateReservation(false);
                      }}
                      onCancel={() => setShowCreateReservation(false)}
                    />
                  ) : (
                    <>
                      <ReservationTypeahead
                        value={selectedReservationOption}
                        onChange={setSelectedReservationOption}
                        placeholder="Search reservations by ref or customer..."
                        disabled={associationSaving}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          className="btn-primary"
                          disabled={!selectedReservationOption || associationSaving}
                          onClick={() => {
                            if (selectedReservationOption) {
                              saveAssociations({ reservation_id: selectedReservationOption.id }, 'Reservation association updated');
                            }
                          }}
                        >
                          Link selected
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={associationSaving || !currentReservationId}
                          onClick={() => saveAssociations({ reservation_id: null }, 'Reservation unlinked')}
                        >
                          Clear
                        </button>
                      </div>
                      <div className="space-y-2 border-t border-ghost pt-2">
                        <p className="text-xs font-medium text-on-surface-variant">Or set a manual reservation reference</p>
                        <input
                          type="text"
                          className="input-field w-full text-sm"
                          placeholder="Example: RES-2026-001"
                          value={manualReservationRef}
                          onChange={(e) => setManualReservationRef(e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn-secondary w-full"
                          disabled={associationSaving}
                          onClick={() => saveAssociations({ reservation_id: manualReservationRef.trim() || null }, 'Reservation reference updated')}
                        >
                          Save reference
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <button
                  type="button"
                  className="btn-secondary w-full"
                  onClick={() => {
                    onClose();
                    navigate('/admin/reservations');
                  }}
                >
                  Open reservations page →
                </button>
              </>
            )}

            {secondaryView === 'markPaid' && (
              <>
                <div className="card p-4">
                  <p className="text-sm font-medium text-on-surface">Manual payment details</p>
                  <p className="mt-1 text-xs text-on-surface-variant">
                    This immediately marks the invoice as paid, clears any active payment link, and stores your audit context.
                  </p>
                </div>

                <form
                  className="rounded-card border border-ghost bg-surface-container-low p-3 space-y-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void submitMarkPaidFromDrawer();
                  }}
                  onKeyDown={handleMarkPaidPanelKeyDown}
                >
                  <div>
                    <label htmlFor="drawer-mark-paid-method" className="mb-1 block text-sm font-medium text-on-surface">
                      Payment method
                    </label>
                    <select
                      ref={markPaidMethodRef}
                      id="drawer-mark-paid-method"
                      className="input-field w-full"
                      value={markPaidPaymentMethod}
                      onChange={(event) => setMarkPaidPaymentMethod(event.target.value)}
                      disabled={markPaidSubmitting}
                    >
                      {MANUAL_PAYMENT_METHOD_OPTIONS.map((method) => (
                        <option key={method} value={method}>{method.toUpperCase()}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="drawer-mark-paid-note" className="mb-1 block text-sm font-medium text-on-surface">
                      Internal note (optional)
                    </label>
                    <textarea
                      id="drawer-mark-paid-note"
                      className="input-field min-h-[120px] w-full"
                      maxLength={1000}
                      value={markPaidNote}
                      onChange={(event) => setMarkPaidNote(event.target.value)}
                      placeholder="Check/reference number, source confirmation, or reconciliation context"
                      disabled={markPaidSubmitting}
                    />
                  </div>

                  <p className="text-xs text-on-surface-variant">
                    Keyboard: Enter confirms from payment method, Cmd/Ctrl+Enter confirms from note, Esc closes this panel.
                  </p>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setSecondaryView(null)}
                      disabled={markPaidSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={markPaidSubmitting}
                    >
                      {markPaidSubmitting ? 'Marking...' : 'Confirm Mark Paid'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        ),
      }
    : undefined;

  return (
    <>
      <EntityDrawer
        open={open}
        title={isEditing ? 'Edit Invoice' : 'Invoice Details'}
        subtitle={`${selectedInvoice.invoice_number} · ${selectedInvoice.customer_details.email}`}
        onClose={onClose}
        widthClassName="max-w-3xl"
        secondary={secondary}
      >
      <div className="space-y-6">

        {isEditing ? (
          /* ══ EDIT MODE ══ */
          <>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="inline-flex items-center gap-1 text-sm text-on-surface-variant transition-colors hover:text-on-surface"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Back to details
            </button>
            <InvoiceEditorForm
              description={editDescription}
              setDescription={setEditDescription}
              lineItems={editLineItems}
              addLineItem={() =>
                setEditLineItems([
                  ...editLineItems,
                  { id: Date.now().toString(), description: '', quantity: 1, unit_amount: 0 },
                ])
              }
              removeLineItem={(id) => {
                if (editLineItems.length > 1) {
                  setEditLineItems(editLineItems.filter((item) => item.id !== id));
                }
              }}
              updateLineItem={(id, field, value) =>
                setEditLineItems(
                  editLineItems.map((item) =>
                    item.id === id ? { ...item, [field]: value } : item
                  )
                )
              }
              discountAmount={editDiscountAmount}
              setDiscountAmount={setEditDiscountAmount}
              taxPercentage={editTaxPercentage}
              setTaxPercentage={setEditTaxPercentage}
              paymentTerms={editPaymentTerms}
              setPaymentTerms={setEditPaymentTerms}
              dueDate={editDueDate}
              setDueDate={setEditDueDate}
              memo={editMemo}
              setMemo={setEditMemo}
              subtotal={editSubtotal}
              taxAmount={editTaxAmount}
              total={editTotal}
              formatCurrency={formatEditCurrency}
              onSubmit={handleEditSubmit}
              onCancel={() => setIsEditing(false)}
              submitLabel="Save Changes"
              isSubmitting={isSaving}
            />
          </>
        ) : (
          /* ══ NORMAL TABBED VIEW ══ */
          <>
        {/* ── Tab navigation ── */}
        <div className="overflow-x-auto">
          <Tabs
            tabs={tabs.map((tab) => ({ key: tab.id, label: tab.label }))}
            activeTab={drawerTab}
            onChange={(tab) => setDrawerTab(tab as InvoiceDrawerTab)}
            className="min-w-max"
          />
        </div>

        {/* ── Persistent action bar ── */}
        <div className="flex flex-wrap items-center gap-2">
          {selectedInvoice.status === 'draft' && (
            <button
              type="button"
              onClick={enterEditMode}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm"
            >
              <PencilSquareIcon className="h-4 w-4" />
              Edit
            </button>
          )}
          {selectedInvoice.status === 'open' && onMarkPaidSubmit && !groupedPaymentLockActive && (
            <button
              type="button"
              onClick={() => setSecondaryView(secondaryView === 'markPaid' ? null : 'markPaid')}
              className="btn-secondary text-sm"
              disabled={markPaidSubmitting}
            >
              {markPaidSubmitting ? 'Marking...' : 'Mark Paid'}
            </button>
          )}
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={() => {
                onAction(action.label);
                action.onClick();
              }}
              className={
                action.dangerous
                  ? 'inline-flex items-center rounded-pill px-5 py-2 text-sm font-medium text-white transition-all bg-red-600 hover:bg-red-700'
                  : 'btn-secondary text-sm'
              }
            >
              {action.label}
            </button>
          ))}
        </div>

        {groupedPaymentLockActive && (
          <div className="rounded-card border border-info-container/70 bg-info-container/25 px-4 py-3 text-sm text-on-surface">
            This invoice is part of an active grouped payment request. Use the grouped request actions for payment links, sending, manual payment, or invalidation.
          </div>
        )}

        {syncWarning}

        {/* ══ DETAILS TAB ══ */}
        {drawerTab === 'details' && (
          <div className="space-y-4">

            {/* Invoice summary */}
            <div className="card p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Invoice Number</p>
                  <p className="mt-1 text-lg font-semibold text-on-surface">{selectedInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Status</p>
                  <div className="mt-1.5"><InvoiceStatusBadge status={normalizedStatus} /></div>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">{selectedInvoice.status === 'draft' ? 'Draft Bill-To' : 'Historical Bill-To'}</p>
                  <p className="mt-1 font-medium text-on-surface">{currentCustomerDetails.name}</p>
                  <p className="text-sm text-on-surface-variant">{currentCustomerDetails.email}</p>
                  {selectedInvoice.customer_details.company_name && (
                    <p className="text-sm text-on-surface-variant">{selectedInvoice.customer_details.company_name}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Total Amount</p>
                  <p className="mt-1 text-lg font-bold text-primary">{formatCurrencyCents(selectedInvoice.total, selectedInvoice.currency)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Issued</p>
                  <p className="mt-1 text-on-surface">{formatDateShort(selectedInvoice.issued_at)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Due</p>
                  <p className="mt-1 text-on-surface">{formatDateShort(selectedInvoice.due_date)}</p>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <h4 className="mb-3 text-sm font-semibold text-on-surface">Lineage</h4>
              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                <div className="rounded-card bg-surface-container-low p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Customer</p>
                  <p className="mt-1 truncate font-medium text-on-surface">{currentCustomerDetails?.name || linkedCustomerName || 'Not linked'}</p>
                  <p className="truncate text-xs text-on-surface-variant">{currentCustomerDetails?.email || currentCustomerId || 'No customer reference'}</p>
                </div>
                <div className="rounded-card bg-surface-container-low p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Account</p>
                  <p className="mt-1 truncate font-medium text-on-surface">{linkedAccount?.name || selectedInvoice.accountSnapshots?.[0]?.name || 'Not linked'}</p>
                  <p className="truncate text-xs text-on-surface-variant">{currentAccountId || 'No account reference'}</p>
                </div>
                <div className="rounded-card bg-surface-container-low p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Reservation</p>
                  <p className="mt-1 truncate font-medium text-on-surface">{reservationRef || 'Not linked'}</p>
                  <p className="truncate text-xs text-on-surface-variant">{currentReservationId || 'No reservation reference'}</p>
                </div>
                <div className="rounded-card bg-surface-container-low p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Billing Source</p>
                  <p className="mt-1 truncate font-medium text-on-surface">
                    {selectedInvoice.grouped_payment_request_id
                      ? 'Grouped payment request'
                      : selectedInvoice.metadata?.source || selectedInvoice.metadata?.import_source || 'Direct invoice'}
                  </p>
                  <p className="truncate text-xs text-on-surface-variant">
                    {selectedInvoice.grouped_payment_request_id ||
                      selectedInvoice.stripe_invoice_id ||
                      selectedInvoice.payment_intent_id ||
                      'No external billing reference'}
                  </p>
                </div>
              </div>
            </div>

            <InvoiceContactEditor key={`${selectedInvoice.id}:${selectedInvoice.status}:${contactInvoice.metadata?.local_customer_id || ''}`}
              invoice={contactInvoice} onSaved={(updated) => {
                setContactInvoice(updated as Invoice);
                setCurrentCustomerDetails(updated.customer_details as Invoice['customer_details']);
              }} />

            <RecordContactSnapshotsPanel
              snapshots={selectedInvoice.contactSnapshots}
              subtitle="Invoice recipient, billing contact, and matched customer saved with this invoice."
            />

            {/* Associations — customer, account, reservation */}
            <div className="card p-4">
              <h4 className="mb-3 text-sm font-semibold text-on-surface">Associations</h4>
              <div className="space-y-3">
                {/* Customer */}
                <button
                  type="button"
                  onClick={() => setSecondaryView(secondaryView === 'customer' ? null : 'customer')}
                  className={`flex w-full items-center justify-between gap-3 rounded-card border p-3 text-left transition-colors hover:bg-surface-container-low ${secondaryView === 'customer' ? 'border-primary bg-surface-container-low' : 'border-ghost'}`}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <UserCircleIcon className="h-4 w-4 flex-shrink-0 text-on-surface-variant" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-on-surface">{currentCustomerDetails?.name || '—'}</p>
                      <p className="text-xs text-on-surface-variant">{currentCustomerDetails?.email || 'No customer linked'}</p>
                    </div>
                  </div>
                  <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${secondaryView === 'customer' ? 'rotate-180 text-primary' : 'text-on-surface-variant'}`} />
                </button>

                {/* Account */}
                <button
                  type="button"
                  onClick={() => setSecondaryView(secondaryView === 'account' ? null : 'account')}
                  className={`flex w-full items-center justify-between gap-3 rounded-card border p-3 text-left transition-colors hover:bg-surface-container-low ${secondaryView === 'account' ? 'border-primary bg-surface-container-low' : 'border-ghost'}`}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <BuildingOfficeIcon className="h-4 w-4 flex-shrink-0 text-on-surface-variant" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-on-surface">{linkedAccount?.name ?? '—'}</p>
                      <p className="text-xs text-on-surface-variant">{currentAccountId ? 'Billing account' : 'No account linked'}</p>
                    </div>
                  </div>
                  <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${secondaryView === 'account' ? 'rotate-180 text-primary' : 'text-on-surface-variant'}`} />
                </button>

                {/* Reservation */}
                <button
                  type="button"
                  onClick={() => setSecondaryView(secondaryView === 'reservation' ? null : 'reservation')}
                  className={`flex w-full items-center justify-between gap-3 rounded-card border p-3 text-left transition-colors hover:bg-surface-container-low ${secondaryView === 'reservation' ? 'border-primary bg-surface-container-low' : 'border-ghost'}`}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 flex-shrink-0 text-on-surface-variant" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-on-surface">
                        {reservationRef
                          ? (reservationRef.length > 20 ? `${reservationRef.slice(0, 8)}…` : reservationRef)
                          : '—'}
                      </p>
                      <p className="text-xs text-on-surface-variant">{currentReservationId ? 'Linked reservation' : 'No reservation linked'}</p>
                    </div>
                  </div>
                  <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${secondaryView === 'reservation' ? 'rotate-180 text-primary' : 'text-on-surface-variant'}`} />
                </button>
              </div>
            </div>

            {/* Line items */}
            <div className="card p-4">
              <h4 className="mb-3 text-sm font-semibold text-on-surface">
                Line Items · {formatLineItemCount(selectedInvoice.line_items?.length)}
              </h4>
              {selectedInvoice.line_items && selectedInvoice.line_items.length > 0 ? (
                <div className="overflow-hidden rounded-card border border-ghost">
                  <table className="min-w-full divide-y divide-outline">
                    <thead className="bg-surface-container-low">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant">Description</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-on-surface-variant">Qty</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-on-surface-variant">Unit Price</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-on-surface-variant">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline">
                      {selectedInvoice.line_items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-sm text-on-surface">{item.description || 'No description'}</td>
                          <td className="px-4 py-2 text-sm text-on-surface text-right">{item.quantity}</td>
                          <td className="px-4 py-2 text-sm text-on-surface text-right">
                            {formatCurrencyCents(item.unit_amount, selectedInvoice.currency)}
                          </td>
                          <td className="px-4 py-2 text-sm font-medium text-on-surface text-right">
                            {formatCurrencyCents(item.amount, selectedInvoice.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-on-surface-variant">No line items on this invoice.</p>
              )}
            </div>

            {/* Payment link */}
            {selectedInvoice.payment_url && (
              <div className="card p-4">
                <h4 className="mb-3 text-sm font-semibold text-on-surface">Payment Link</h4>
                <div className="flex items-center gap-2">
                  <a
                    href={selectedInvoice.payment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="min-w-0 flex-1 truncate text-sm text-primary hover:underline"
                  >
                    {selectedInvoice.payment_url}
                  </a>
                  <button
                    type="button"
                    className="btn-secondary inline-flex flex-shrink-0 items-center gap-1.5 text-sm"
                    onClick={() => {
                      navigator.clipboard.writeText(selectedInvoice.payment_url || '');
                      toast.success('Payment link copied!');
                    }}
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* Fee breakdown */}
            {selectedInvoice.status === 'paid' && feeBreakdown && (
              <div className="card p-4">
                <h4 className="mb-3 text-sm font-semibold text-on-surface">Fee Breakdown</h4>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">Amount Paid</dt>
                    <dd className="font-medium text-on-surface">{formatCurrencyCents(feeBreakdown.amountPaid, selectedInvoice.currency)}</dd>
                  </div>
                  {feeBreakdown.stripeFee != null && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Processing Fee</dt>
                      <dd className="text-on-surface">-{formatCurrencyCents(feeBreakdown.stripeFee, selectedInvoice.currency)}</dd>
                    </div>
                  )}
                  {feeBreakdown.platformFee != null && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Platform Fee</dt>
                      <dd className="text-on-surface">-{formatCurrencyCents(feeBreakdown.platformFee, selectedInvoice.currency)}</dd>
                    </div>
                  )}
                  {feeBreakdown.totalFees != null && (
                    <div className="flex justify-between gap-2 border-t border-ghost pt-2">
                      <dt className="text-on-surface-variant">Total Fees</dt>
                      <dd className="text-on-surface">-{formatCurrencyCents(feeBreakdown.totalFees, selectedInvoice.currency)}</dd>
                    </div>
                  )}
                  {feeBreakdown.netAmount != null && (
                    <div className="flex justify-between gap-2">
                      <dt className="font-medium text-on-surface">Net to Account</dt>
                      <dd className="font-semibold text-on-surface">{formatCurrencyCents(feeBreakdown.netAmount, selectedInvoice.currency)}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {selectedInvoice.status === 'paid' && paymentActivityLoading && !feeBreakdown && (
              <div className="card space-y-2 p-4">
                <SkeletonLine className="mb-1 h-3 w-28" />
                <SkeletonLine className="h-4 w-40" />
                <SkeletonLine className="h-4 w-36" />
                <SkeletonLine className="h-4 w-44" />
              </div>
            )}

            {selectedInvoice.status === 'paid' && manualPaymentMetadata && (
              <div className="card p-4">
                <h4 className="mb-3 text-sm font-semibold text-on-surface">Manual Payment</h4>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">Method</dt>
                    <dd className="font-medium text-on-surface">{manualPaymentMetadata.manual_payment_method}</dd>
                  </div>
                  {manualPaymentMetadata.manual_payment_marked_at && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Marked Paid</dt>
                      <dd className="text-on-surface">{formatActivityTimestamp(manualPaymentMetadata.manual_payment_marked_at)}</dd>
                    </div>
                  )}
                  {manualPaymentMetadata.manual_payment_stripe_invoice_action && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Stripe Invoice</dt>
                      <dd className="text-on-surface">{manualPaymentMetadata.manual_payment_stripe_invoice_action}</dd>
                    </div>
                  )}
                  {manualPaymentMetadata.manual_payment_note && (
                    <div className="border-t border-ghost pt-2">
                      <dt className="text-on-surface-variant">Note</dt>
                      <dd className="mt-1 whitespace-pre-wrap text-on-surface">{manualPaymentMetadata.manual_payment_note}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>
        )}

        {/* ══ ACTIVITY TAB ══ */}
        {drawerTab === 'activity' && (
          <div className="space-y-4">
            {paymentActivityLoading ? (
              <div className="space-y-4">
                <div className="card space-y-3 p-4">
                  <SkeletonLine className="h-3 w-40" />
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <SkeletonLine className="h-4 w-32" />
                    <SkeletonLine className="h-4 w-28" />
                    <SkeletonLine className="h-4 w-36" />
                    <SkeletonLine className="h-4 w-24" />
                  </div>
                </div>
                <div className="card space-y-3 p-4">
                  <SkeletonLine className="h-3 w-32" />
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="relative pl-5">
                      <SkeletonBadge className="absolute left-0 top-1.5 h-2.5 w-2.5" />
                      <div className="rounded-card border border-ghost bg-surface-container-low p-3 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <SkeletonLine className="h-4 w-32" />
                          <SkeletonLine className="h-3 w-20" />
                        </div>
                        <SkeletonLine className="h-4 w-5/6" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : paymentActivity ? (
              <>
                {/* Payment status */}
                <div className="card p-4">
                  <h4 className="mb-3 text-sm font-semibold text-on-surface">Payment Status</h4>
                  <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                    <div>
                      <dt className="text-on-surface-variant">Modal status</dt>
                      <dd className="mt-0.5 font-medium capitalize text-on-surface">
                        {paymentActivity.diagnostics?.payment_state?.replace(/_/g, ' ') || normalizedStatus}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-on-surface-variant">Stripe status</dt>
                      <dd className="mt-0.5 font-medium capitalize text-on-surface">
                        {paymentActivity.diagnostics?.stripe_status || paymentActivity.stripe.status || 'Not linked'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-on-surface-variant">Balance due</dt>
                      <dd className="mt-0.5 font-medium text-on-surface">
                        {formatCurrencyCents(
                          paymentActivity.diagnostics?.balance_due ?? selectedInvoice.amount_due,
                          selectedInvoice.currency
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-on-surface-variant">Webhook state</dt>
                      <dd className="mt-0.5 font-medium text-on-surface">
                        {paymentActivity.diagnostics?.webhook_event_state?.replace(/_/g, ' ') || '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-on-surface-variant">Last Paid</dt>
                      <dd className="mt-0.5 font-medium text-on-surface">
                        {formatActivityTimestamp(paymentActivity.stripe.status_transitions?.paid_at || selectedInvoice.paid_at)}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Payment references */}
                {(paymentActivity.stripe.payment_intent_id || isPlatformAdmin) && (
                  <div className="card p-4">
                    <h4 className="mb-3 text-sm font-semibold text-on-surface">Payment References</h4>
                    <dl className="space-y-3 text-sm">
                      {paymentActivity.stripe.payment_intent_id && (
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <dt className="text-on-surface-variant">Payment Intent</dt>
                          <dd className="flex items-center gap-2">
                            <span className="font-mono text-xs font-medium text-on-surface">
                              {paymentActivity.stripe.payment_intent_id}
                            </span>
                            {getStripeObjectUrl(paymentActivity.stripe.payment_intent_id, paymentActivity.connected_account_id) && (
                              <a
                                href={getStripeObjectUrl(paymentActivity.stripe.payment_intent_id, paymentActivity.connected_account_id) || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                              >
                                Open
                                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </dd>
                        </div>
                      )}
                      {isPlatformAdmin && (
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <dt className="text-on-surface-variant">Connected Account</dt>
                          <dd className="break-all font-mono text-xs font-medium text-on-surface">
                            {paymentActivity.connected_account_id || 'N/A'}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}

                {/* Activity timeline */}
                <div className="card p-4">
                  <h4 className="mb-3 text-sm font-semibold text-on-surface">Activity Timeline</h4>
                  {(() => {
                    const timeline = [
                      {
                        id: `invoice-created-${selectedInvoice.id}`,
                        title: 'Invoice created',
                        occurredAt: selectedInvoice.created_at,
                        detail: `Invoice ${selectedInvoice.invoice_number} created`,
                        isError: false,
                      },
                      ...paymentActivity.webhook_events.map((event) => ({
                        id: event.id,
                        title: humanizeWebhookEvent(event.event_type),
                        occurredAt: event.created_at,
                        detail: event.last_error ? `Error: ${event.last_error}` : null,
                        isError: !!event.last_error || event.status === 'failed',
                      })),
                    ].sort((a, b) => activitySortValue(b.occurredAt) - activitySortValue(a.occurredAt));

                    if (timeline.length === 0) {
                      return <p className="text-sm text-on-surface-variant">No activity recorded yet.</p>;
                    }

                    return (
                      <ol className="space-y-3">
                        {timeline.map((item) => (
                          <li key={item.id} className="relative pl-5">
                            <span
                              className={`absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full ${
                                item.isError ? 'bg-red-500' : 'bg-primary'
                              }`}
                            />
                            <div className="rounded-card border border-ghost bg-surface-container-low p-3">
                              <div className="flex items-center justify-between gap-3">
                                <p className="text-sm font-semibold text-on-surface">{item.title}</p>
                                <p className="text-xs text-on-surface-variant">{formatActivityTimestamp(item.occurredAt)}</p>
                              </div>
                              {item.detail && (
                                <p className="mt-1 text-sm text-on-surface-variant">{item.detail}</p>
                              )}
                            </div>
                          </li>
                        ))}
                      </ol>
                    );
                  })()}
                </div>
              </>
            ) : (
              <div className="card p-4">
                <p className="text-sm text-on-surface-variant">No payment activity available.</p>
              </div>
            )}
          </div>
        )}

        {/* ══ FILES TAB ══ */}
        {drawerTab === 'files' && (
          <div className="card p-4">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-on-surface">Attachments</h4>
              <button
                type="button"
                onClick={onUploadFileClick}
                disabled={uploadingAttachment}
                className="btn-secondary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
              >
                {uploadingAttachment ? (
                  'Uploading...'
                ) : (
                  <>
                    <PlusIcon className="h-3.5 w-3.5" />
                    Add File
                  </>
                )}
              </button>
            </div>
            {selectedInvoice.attachments && selectedInvoice.attachments.length > 0 ? (
              <div className="divide-y divide-outline overflow-hidden rounded-card border border-ghost">
                {selectedInvoice.attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between px-4 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <PaperClipIcon className="h-4 w-4 flex-shrink-0 text-on-surface-variant" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-on-surface">{attachment.original_name}</p>
                        <p className="text-xs text-on-surface-variant">
                          {formatFileSize(attachment.size)} · {formatDateShort(attachment.uploaded_at)}
                        </p>
                      </div>
                    </div>
                    <div className="ml-4 flex flex-shrink-0 items-center gap-3">
                      <button
                        type="button"
                        onClick={() => onAttachmentDownload(attachment.id, attachment.original_name)}
                        className="text-on-surface-variant transition-colors hover:text-primary"
                        title="Download"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onAttachmentDelete(attachment.id)}
                        className="text-on-surface-variant transition-colors hover:text-red-600"
                        title="Remove"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-card border border-ghost px-6 py-8 text-center">
                <PaperClipIcon className="mx-auto h-8 w-8 text-on-surface-variant/40" />
                <p className="mt-2 text-sm text-on-surface-variant">No attachments yet.</p>
                <p className="mt-1 text-xs text-on-surface-variant">
                  Upload files to keep supporting documents alongside this invoice.
                </p>
              </div>
            )}
          </div>
        )}

          </>
        )}
      </div>
      </EntityDrawer>


    </>
  );
}
